/**
 * Money domain data layer — payments, bookings (amenities + venues) and
 * helpdesk tickets. Unlike src/components/gate/api.ts and
 * src/features/community/lib.ts (which guard against endpoints still being
 * built), these endpoints are LIVE and documented (apps/api/app/blueprints/
 * payments.py, bookings.py, helpdesk.py) — so mappers are typed against the
 * real to_dict() shapes with light defensive coercion, not multi-key tolerant
 * guessing. Amounts stay in paise here; format with formatINR() at render time.
 */
import React from 'react';

import type { ApiError } from '@bero/api-client';
import { api } from '@/lib/client';

type Raw = Record<string, unknown>;

function asRaw(v: unknown): Raw {
  return v && typeof v === 'object' ? (v as Raw) : {};
}
function str(o: Raw, key: string, fb = ''): string {
  const v = o[key];
  return typeof v === 'string' ? v : v == null ? fb : String(v);
}
function strOrNull(o: Raw, key: string): string | null {
  const v = o[key];
  return typeof v === 'string' && v.length > 0 ? v : null;
}
function num(o: Raw, key: string, fb = 0): number {
  const v = o[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : fb;
}
function boolOf(o: Raw, key: string, fb = false): boolean {
  const v = o[key];
  return typeof v === 'boolean' ? v : fb;
}
function arr(o: Raw, key: string): unknown[] {
  const v = o[key];
  return Array.isArray(v) ? v : [];
}

/** True if this failure was "not found" (used to distinguish empty-state from real errors). */
export function isNotFound(e: unknown): boolean {
  return !!e && typeof e === 'object' && (e as ApiError).status === 404;
}
/** True if this failure was a conflict (slot/venue already taken). */
export function isConflict(e: unknown): boolean {
  return !!e && typeof e === 'object' && (e as ApiError).status === 409;
}
export function errorMessage(e: unknown, fallback: string): string {
  if (e && typeof e === 'object' && typeof (e as ApiError).error === 'string') return (e as ApiError).error;
  return fallback;
}

/**
 * Collapses an arbitrary-length deps array into a version number that only
 * changes when the deps shallow-differ, so it can be passed to useCallback
 * below as a literal-array dependency (callers still pass a plain deps list).
 */
function useDepsVersion(deps: React.DependencyList): number {
  const [tracked, setTracked] = React.useState({ deps, version: 0 });
  const changed = deps.length !== tracked.deps.length || deps.some((d, i) => !Object.is(d, tracked.deps[i]));
  if (changed) setTracked({ deps, version: tracked.version + 1 });
  return tracked.version;
}

/* ---------------- shared fetch hook (mirrors features/community/lib useFetch) ---------------- */
export function useAsync<T>(load: () => Promise<T>, deps: React.DependencyList) {
  const [data, setData] = React.useState<T | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);
  const seq = React.useRef(0);
  const depsVersion = useDepsVersion(deps);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const refetch = React.useCallback(async () => {
    const id = ++seq.current;
    setLoading(true);
    setError(false);
    try {
      const d = await load();
      if (id !== seq.current) return;
      setData(d);
      setLoading(false);
    } catch {
      if (id !== seq.current) return;
      setData(null);
      setError(true);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depsVersion]);

  React.useEffect(() => { void Promise.resolve().then(() => refetch()); }, [refetch]);
  const mutate = React.useCallback((updater: (prev: T | null) => T | null) => setData(updater), []);
  return { data, loading, error, refetch, mutate };
}

/* =================== Payments =================== */

export type BreakdownLine = { label: string; amount: number };
export type BillPayment = { id: number; billId: number; amount: number; method: string; reference: string; paidAt: string };
export type BillStatus = 'DUE' | 'PAID' | 'OVERDUE';
export type Bill = {
  id: number; period: string; amount: number; breakdown: BreakdownLine[];
  dueDate: string; lateFee: number; totalAmount: number; status: BillStatus;
  flatCode: string | null; payments: BillPayment[];
};

function toBillPayment(raw: unknown): BillPayment {
  const o = asRaw(raw);
  return { id: num(o, 'id'), billId: num(o, 'billId'), amount: num(o, 'amount'), method: str(o, 'method', 'UPI'), reference: str(o, 'reference'), paidAt: str(o, 'paidAt') };
}

function toBill(raw: unknown): Bill {
  const o = asRaw(raw);
  const status = str(o, 'status', 'DUE');
  return {
    id: num(o, 'id'), period: str(o, 'period'), amount: num(o, 'amount'),
    breakdown: arr(o, 'breakdown').map((b) => { const bo = asRaw(b); return { label: str(bo, 'label'), amount: num(bo, 'amount') }; }),
    dueDate: str(o, 'dueDate'), lateFee: num(o, 'lateFee'), totalAmount: num(o, 'totalAmount', num(o, 'amount')),
    status: status === 'PAID' ? 'PAID' : status === 'OVERDUE' ? 'OVERDUE' : 'DUE',
    flatCode: strOrNull(o, 'flatCode'),
    payments: arr(o, 'payments').map(toBillPayment),
  };
}

/** Current month's bill, or null when there is none (404 = no bill this period). */
function isNoFlatErr(e: unknown): boolean {
  const err = e as { status?: number; error?: string };
  return err?.status === 400 && /no flat/i.test(err?.error ?? '');
}

export async function fetchCurrentBill(): Promise<Bill | null> {
  try {
    const body = await api.get<{ bill: unknown }>('/api/payments/current');
    return toBill(body.bill);
  } catch (e) {
    if (isNotFound(e) || isNoFlatErr(e)) return null;  // no bill / no flat → "No dues"
    throw e;
  }
}

export async function fetchBillHistory(per = 50): Promise<Bill[]> {
  const body = await api.get<{ items: unknown[] }>(`/api/payments/history?per=${per}`);
  return (body.items ?? []).map(toBill);
}

export type PaymentResult = { reference: string; amount: number; method: string; paidAt: string };

/* Members never pay in-app (user decision 2026-07-17): they pay the society's
 * bank/UPI directly and the Treasurer records receipt (bye-law 113). */

export type PayTo = { bankName: string | null; bankAccount: string | null; bankIfsc: string | null; upiVpa: string | null };

export async function fetchPayTo(): Promise<PayTo | null> {
  try {
    const body = await api.get<{ payTo: PayTo | null }>('/api/payments/pay-info');
    return body.payTo ?? null;
  } catch {
    return null;
  }
}

export type OutstandingBill = Bill & { flatCode: string | null };

/** Treasurer worklist: unpaid bills across the society (403 for everyone else). */
export async function fetchOutstanding(): Promise<OutstandingBill[]> {
  const body = await api.get<{ items: unknown[] }>('/api/payments/outstanding');
  return (body.items ?? []).map((raw) => {
    const o = asRaw(raw);
    return { ...toBill(raw), flatCode: str(o, 'flatCode') || null };
  });
}

/** Treasurer records a real-world receipt against a bill (bye-law 113). */
export async function recordPayment(billId: number, method: string, reference: string): Promise<PaymentResult> {
  const body = await api.post<{ payment: unknown }>(`/api/payments/${billId}/record`, { method, reference });
  const p = asRaw(body.payment);
  return { reference: str(p, 'reference'), amount: num(p, 'amount'), method: str(p, 'method', method), paidAt: str(p, 'paidAt') };
}

/* =================== Bookings: amenities =================== */

export type AmenitySlot = { slotStart: string; slotEnd: string | null; state: 'free' | 'booked'; bookedBy: string | null; bookingId: number | null };
export type Amenity = {
  id: number; name: string; openHour: number; closeHour: number; slotMinutes: number; date: string;
  rules: string[]; isPaid: boolean; feePaise: number; icon: string; slots: AmenitySlot[];
};

function slotEndOf(start: string, minutes: number): string | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(start);
  if (!m) return null;
  const total = Number(m[1]) * 60 + Number(m[2]) + minutes;
  const h = Math.floor(total / 60) % 24;
  const mm = total % 60;
  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

function toAmenity(raw: unknown): Amenity {
  const o = asRaw(raw);
  const slotMinutes = num(o, 'slotMinutes', 60);
  return {
    id: num(o, 'id'), name: str(o, 'name'), openHour: num(o, 'openHour'), closeHour: num(o, 'closeHour'),
    slotMinutes, date: str(o, 'date'),
    rules: arr(o, 'rules').map((r) => String(r)),
    isPaid: boolOf(o, 'isPaid'), feePaise: num(o, 'feePaise'), icon: str(o, 'icon'),
    slots: arr(o, 'slots').map((s) => {
      const so = asRaw(s);
      const slotStart = str(so, 'slotStart');
      return {
        slotStart, slotEnd: slotEndOf(slotStart, slotMinutes),
        state: str(so, 'state') === 'booked' ? 'booked' : 'free',
        bookedBy: strOrNull(so, 'bookedBy'),
        bookingId: typeof so.bookingId === 'number' ? so.bookingId : null,
      };
    }),
  };
}

export async function fetchAmenities(): Promise<Amenity[]> {
  const body = await api.get<{ amenities: unknown[] }>('/api/bookings/amenities');
  return (body.amenities ?? []).map(toAmenity);
}

export function bookAmenitySlot(amenityId: number, date: string, slot: string) {
  return api.post<{ booking: unknown }>(`/api/bookings/amenities/${amenityId}/book`, { date, slot });
}

/* =================== Bookings: venues =================== */

export type Venue = { id: number; name: string; capacity: number; advanceDays: number; fee: number; busyDates: string[] };

function toVenue(raw: unknown): Venue {
  const o = asRaw(raw);
  return {
    id: num(o, 'id'), name: str(o, 'name'), capacity: num(o, 'capacity'),
    advanceDays: num(o, 'advanceDays', 30), fee: num(o, 'fee'),
    busyDates: arr(o, 'busyDates').map((d) => String(d)),
  };
}

export async function fetchVenues(): Promise<Venue[]> {
  const body = await api.get<{ venues: unknown[] }>('/api/bookings/venues');
  return (body.venues ?? []).map(toVenue);
}

export type VenueBookingStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export async function bookVenue(venueId: number, date: string, purpose: string): Promise<{ status: VenueBookingStatus }> {
  const body = await api.post<{ booking: unknown }>(`/api/bookings/venues/${venueId}/book`, { date, purpose });
  const status = str(asRaw(body.booking), 'status', 'PENDING');
  return { status: status === 'APPROVED' ? 'APPROVED' : status === 'REJECTED' ? 'REJECTED' : 'PENDING' };
}

/* =================== Bookings: combined mine + cancel =================== */

export type MyBooking = {
  kind: 'AMENITY' | 'VENUE';
  id: number;
  title: string; // amenityName or venueName
  when: string; // ISO date (slot date, or venue booking date)
  subtitle: string; // slot time range, or purpose
  statusLabel: 'CONFIRMED' | VenueBookingStatus;
  fee: number;
};

function toMyBooking(raw: unknown): MyBooking | null {
  const o = asRaw(raw);
  if (str(o, 'type') === 'AMENITY') {
    const start = str(o, 'slotStart');
    const end = strOrNull(o, 'slotEnd');
    return {
      kind: 'AMENITY', id: num(o, 'id'), title: str(o, 'amenityName', 'Amenity'),
      when: str(o, 'date'), subtitle: end ? `${start} – ${end}` : start,
      statusLabel: 'CONFIRMED', fee: 0,
    };
  }
  if (str(o, 'type') === 'VENUE') {
    const status = str(o, 'status', 'PENDING');
    return {
      kind: 'VENUE', id: num(o, 'id'), title: str(o, 'venueName', 'Venue'),
      when: str(o, 'bookingDate'), subtitle: str(o, 'purpose'),
      statusLabel: status === 'APPROVED' ? 'APPROVED' : status === 'REJECTED' ? 'REJECTED' : 'PENDING',
      fee: num(o, 'fee'),
    };
  }
  return null;
}

export async function fetchMyBookings(): Promise<MyBooking[]> {
  const body = await api.get<{ bookings: unknown[] }>('/api/bookings/mine');
  return (body.bookings ?? []).map(toMyBooking).filter((b): b is MyBooking => b !== null);
}

export function cancelBooking(kind: 'AMENITY' | 'VENUE', id: number) {
  const path = kind === 'AMENITY' ? 'amenity' : 'venue';
  return api.post<{ ok: boolean }>(`/api/bookings/${path}/${id}/cancel`);
}

/* =================== Helpdesk =================== */

export type TicketCategory = 'PLUMBING' | 'ELECTRICAL' | 'SECURITY' | 'LIFT' | 'PEST' | 'CLEANING' | 'OTHER';
export type TicketStatus = 'OPEN' | 'IN_PROGRESS' | 'URGENT' | 'RESOLVED' | 'CLOSED';

const CATEGORIES: TicketCategory[] = ['PLUMBING', 'ELECTRICAL', 'SECURITY', 'LIFT', 'PEST', 'CLEANING', 'OTHER'];
const STATUSES: TicketStatus[] = ['OPEN', 'IN_PROGRESS', 'URGENT', 'RESOLVED', 'CLOSED'];

export type Ticket = {
  id: number; ticketNo: string; category: TicketCategory; status: TicketStatus;
  title: string; description: string; location: 'PERSONAL' | 'COMMON_AREA';
  assignee: string | null; eta: string | null; hasPhotos: boolean;
  raisedBy: string | null; flat: string | null; createdAt: string; updatedAt: string;
};

export type TicketUpdate = {
  id: number; kind: 'STATUS' | 'COMMENT'; status: string | null; title: string | null;
  body: string; author: string; createdAt: string;
};

export type TicketDetail = Ticket & { timeline: TicketUpdate[] };

function toTicket(raw: unknown): Ticket {
  const o = asRaw(raw);
  const cat = str(o, 'category');
  const status = str(o, 'status');
  return {
    id: num(o, 'id'), ticketNo: str(o, 'ticketNo'),
    category: (CATEGORIES as string[]).includes(cat) ? (cat as TicketCategory) : 'OTHER',
    status: (STATUSES as string[]).includes(status) ? (status as TicketStatus) : 'OPEN',
    title: str(o, 'title'), description: str(o, 'description'),
    location: str(o, 'location') === 'COMMON_AREA' ? 'COMMON_AREA' : 'PERSONAL',
    assignee: strOrNull(o, 'assignee'), eta: strOrNull(o, 'eta'), hasPhotos: boolOf(o, 'hasPhotos'),
    raisedBy: strOrNull(o, 'raisedBy'), flat: strOrNull(o, 'flat'),
    createdAt: str(o, 'createdAt'), updatedAt: str(o, 'updatedAt'),
  };
}

function toUpdate(raw: unknown): TicketUpdate {
  const o = asRaw(raw);
  return {
    id: num(o, 'id'), kind: str(o, 'kind') === 'COMMENT' ? 'COMMENT' : 'STATUS',
    status: strOrNull(o, 'status'), title: strOrNull(o, 'title'), body: str(o, 'body'),
    author: str(o, 'author', 'Helpdesk'), createdAt: str(o, 'createdAt'),
  };
}

export async function fetchTickets(tab: 'open' | 'resolved'): Promise<{ items: Ticket[]; openCount: number; resolvedCount: number }> {
  const body = await api.get<Raw>(`/api/helpdesk?status=${tab}&per=50`);
  return {
    items: arr(body, 'items').map(toTicket),
    openCount: num(body, 'openCount'),
    resolvedCount: num(body, 'resolvedCount'),
  };
}

export async function fetchTicket(id: string | number): Promise<TicketDetail> {
  const body = await api.get<{ ticket: unknown }>(`/api/helpdesk/${id}`);
  const t = asRaw(body.ticket);
  return { ...toTicket(t), timeline: arr(t, 'timeline').map(toUpdate) };
}

export type NewTicket = {
  title: string; category: TicketCategory; description: string;
  location: 'PERSONAL' | 'COMMON_AREA'; priority: 'NORMAL' | 'URGENT';
  photos?: string | null; // base64 data-URI
};

export async function createTicket(input: NewTicket): Promise<Ticket> {
  const body = await api.post<{ ticket: unknown }>('/api/helpdesk', input);
  return toTicket(body.ticket);
}

export async function addComment(ticketId: string | number, body: string): Promise<TicketUpdate> {
  const res = await api.post<{ update: unknown }>(`/api/helpdesk/${ticketId}/comments`, { body });
  return toUpdate(res.update);
}
